import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Inbox() {
  const { user } = useAuth();
  const { subscribeDm, refreshCounts } = useNotifications();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const activeRef = useRef(null);
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
  };
  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    const unsub = subscribeDm((msg) => {
      if (activeRef.current && msg.thread_id === activeRef.current.thread_id) {
        setMessages((m) => [...m, msg]);
        api.get(`/messages/${msg.thread_id}`).then(() => refreshCounts()).catch((err) => console.warn("Failed to mark thread read", err));
        loadThreads();
        return true; // handled — no toast/badge
      }
      loadThreads();
      return false;
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openThread = async (t) => {
    setActive(t);
    activeRef.current = t;
    const { data } = await api.get(`/messages/${t.thread_id}`);
    setMessages(data);
    setThreads((ts) => ts.map((x) => x.thread_id === t.thread_id ? { ...x, unread: 0 } : x));
    refreshCounts();
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    try {
      const { data } = await api.post("/messages", {
        to_user_id: active.other_user_id,
        body: draft,
      });
      setMessages((m) => [...m, data]);
      setDraft("");
      loadThreads();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 grid md:grid-cols-12 gap-6 h-[calc(100vh-3.5rem)]">
      <aside className="md:col-span-4 bg-white border border-[#E2E4E2] overflow-y-auto" data-testid="inbox-threads">
        <div className="px-4 py-3 border-b border-[#E2E4E2] font-bold tracking-tight">Inbox</div>
        {threads.length === 0 && <div className="p-6 text-sm text-[#68706B]">No conversations yet.</div>}
        {threads.map((t) => (
          <button key={t.thread_id} onClick={() => openThread(t)} className={`w-full text-left p-4 border-b border-[#E2E4E2] hover:bg-[#F0EFEB] ${active?.thread_id === t.thread_id ? "bg-[#F0EFEB]" : ""}`} data-testid={`thread-${t.thread_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">{t.other_user_name}</div>
              {t.unread > 0 && (
                <span className="min-w-5 h-5 px-1.5 bg-[#D96C5B] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`thread-unread-${t.thread_id}`}>{t.unread}</span>
              )}
            </div>
            <div className="text-xs text-[#68706B] line-clamp-1 mt-0.5">{t.last_msg}</div>
          </button>
        ))}
      </aside>

      <section className="md:col-span-8 bg-white border border-[#E2E4E2] flex flex-col">
        {active ? (
          <>
            <div className="px-4 py-3 border-b border-[#E2E4E2] font-bold tracking-tight">{active.other_user_name}</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-messages">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] px-3 py-2 text-sm ${m.from_user_id === user.user_id ? "ml-auto bg-[#1E3F33] text-white" : "bg-[#F0EFEB] text-[#1A1E1C]"}`}>
                  {m.is_resume_request && <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">Resume request</div>}
                  {m.body}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t border-[#E2E4E2] flex gap-2">
              <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…" className="rounded-none h-11" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="msg-input" />
              <Button onClick={send} className="rounded-none bg-[#1E3F33] hover:bg-[#152C24] h-11" data-testid="msg-send">Send</Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#68706B] text-sm">Pick a conversation</div>
        )}
      </section>
    </div>
  );
}
